import { deltaTone, driveLegs, stepLabel } from "../components/geotab/geotabFormat.js";
import { resolveDisplayedOperationalSequenceLabel } from "./fleetTimelinePresentation.js";

// Réel Geotab dans le Gantt du Dispatch (voir components/GeotabRealTimelineMarks.jsx) : chaque segment
// porte son avancement et, pour ses deux parties, l'écart de durée du sur place puis de la route vers
// l'étape suivante. Mêmes calculs que « Durées par étape » de l'écran Dispatch vs Geotab.

// Étapes du Gantt couvertes par un élément : un segment seul ou un groupe au même arrêt.
function itemSteps(item) {
  if (item?.kind === "group") {
    const start = Number(item.sameSiteGroupDisplayStartSequence ?? item.startSequence) || 0;
    const end = Number(item.sameSiteGroupDisplayEndSequence ?? item.endSequence) || start;
    return start ? [start, end] : null;
  }
  const numbers = (resolveDisplayedOperationalSequenceLabel(item?.segment).match(/\d+/g) || []).map(Number);
  return numbers.length ? [Math.min(...numbers), Math.max(...numbers)] : null;
}

const duration = (plan, real, tolerance, extra = {}) => ({
  plan, real, gap: real == null ? null : real - plan, tone: real == null ? "empty" : deltaTone(real - plan, tolerance), ...extra,
});

// Avancement : « now » sur place en ce moment, « skip » sauté (une étape suivante est déjà faite).
function progressOf(sites, allSites, inProgress) {
  const found = sites.filter((site) => site.real_arrival != null);
  if (inProgress && found.some((site) => site.open_end)) return "now";
  if (found.length) return "done";
  const lastIdx = Math.max(...sites.map((site) => site.idx));
  return allSites.some((site) => site.idx > lastIdx && site.real_arrival != null) ? "skip" : null;
}

export function buildGeotabRealMarks(items = [], truck = null, tolerance = 15) {
  const cmp = truck?.comparison;
  if (!cmp || !truck.plan) return [];
  const legs = driveLegs(truck);
  const route = (index) => {
    const leg = legs[index];
    return leg ? duration(leg.plan, leg.real, tolerance, { why: leg.why || "", to: cmp.sites[index] ? `${stepLabel(cmp.sites[index].steps)} ${cmp.sites[index].name}` : "dépôt" }) : null;
  };
  return items.flatMap((item) => {
    const source = item?.kind === "group" ? item : item?.segment;
    if (!source) return [];
    const base = {
      key: item.key,
      truckId: String(truck.truck_id || ""),
      leftPercent: Number(source.leftPercent) || 0,
      widthPercent: Number(source.widthPercent) || 0,
      servicePercent: Math.min(Math.max(Number(source.servicePercent) || 0, 0), 100),
    };
    if (source.stopType === "depot_start") {
      return cmp.real_departure == null ? [] : [{ ...base, title: "Inspection et départ", progress: "done", dwell: null, route: route(0) }];
    }
    const steps = itemSteps(item);
    const indexes = steps ? cmp.sites.flatMap((site, index) => ((site.steps || []).some((n) => n >= steps[0] && n <= steps[1]) ? [index] : [])) : [];
    if (!indexes.length) return [];
    const sites = indexes.map((index) => cmp.sites[index]);
    const progress = progressOf(sites, cmp.sites, cmp.in_progress);
    if (!progress) return [];
    const dwellReal = sites.every((site) => site.real_dwell != null) ? sites.reduce((sum, site) => sum + site.real_dwell, 0) : null;
    return [{
      ...base,
      title: `${stepLabel(sites.flatMap((site) => site.steps || []))} ${sites.map((site) => site.name).join(" + ")}`,
      progress,
      // Arrivée sur le site où le camion est encore arrêté.
      since: progress === "now" ? Math.min(...sites.filter((site) => site.open_end && site.real_arrival != null).map((site) => site.real_arrival)) : null,
      dwell: duration(sites.reduce((sum, site) => sum + site.plan_dwell, 0), dwellReal, tolerance, { why: progress === "now" ? "en cours" : "" }),
      route: progress === "done" ? route(indexes[indexes.length - 1] + 1) : null,
    }];
  });
}

// « 2/3 » sous le camion : arrêts terminés sur les arrêts prévus, sans l'inspection ni le chargement de la veille.
// Celui où le camion est encore arrêté n'est pas terminé : il est compté à part, comme les arrêts sautés.
export function summarizeGeotabRealProgress(truck = null) {
  const cmp = truck?.comparison;
  if (!cmp) return null;
  const isOngoing = (site) => Boolean(cmp.in_progress && site.open_end && site.real_arrival != null);
  const ongoing = cmp.sites.filter(isOngoing).length;
  const done = cmp.sites.filter((site) => site.real_arrival != null).length - ongoing;
  const skipped = cmp.sites.filter((site) => site.real_arrival == null && cmp.sites.some((other) => other.idx > site.idx && other.real_arrival != null)).length;
  return { done, ongoing, skipped, total: cmp.sites.length };
}
